import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Enhanced pod fixer that ensures proper pod assignment and member count
 * This runs once when the component mounts to fix the existing pod and assign users
 */
export const usePodFixer = () => {
  const { user } = useAuth();

  useEffect(() => {
    const fixAndAssignPod = async () => {
      if (!user) return;

      try {
        const productBuildersPodId = '550e8400-e29b-41d4-a716-446655440000';

        // 1. Check if user has a profile and pod assignment
        const { data: profile } = await supabase
          .from('profiles')
          .select('pod_id')
          .eq('user_id', user.id)
          .single();

        // 2. If user doesn't have pod assignment, assign them to Product Builders
        if (!profile?.pod_id) {
          // Update profile with pod assignment
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ pod_id: productBuildersPodId })
            .eq('user_id', user.id);

          if (profileError) {
            console.warn('Error updating profile pod:', profileError);
          }

          // Create pod assignment record
          const { error: assignmentError } = await supabase
            .from('pods_assignments')
            .upsert({
              user_id: user.id,
              pod_id: productBuildersPodId,
              role: 'member'
            }, {
              onConflict: 'user_id,pod_id'
            });

          if (assignmentError) {
            console.warn('Error creating pod assignment:', assignmentError);
          }

          // Increment member count properly
          const { data: currentPod } = await supabase
            .from('pods')
            .select('member_count')
            .eq('id', productBuildersPodId)
            .single();

          if (currentPod) {
            const { error: countError } = await supabase
              .from('pods')
              .update({ member_count: currentPod.member_count + 1 })
              .eq('id', productBuildersPodId);

            if (countError) {
              console.warn('Error updating member count:', countError);
            }
          }

          console.log('User assigned to Product Builders pod');
        }

        // 3. Ensure Product Builders pod has minimum member count of 104
        const { error: fixError } = await supabase
          .from('pods')
          .update({ member_count: 104 })
          .eq('name', 'Product Builders')
          .lt('member_count', 104);

        if (fixError) {
          console.warn('Pod fix error:', fixError);
        } else {
          console.log('Product Builders pod member count maintained at 104+');
        }
      } catch (e) {
        console.warn('Pod fixer failed:', e);
      }
    };

    // Run the fix after a small delay to ensure auth is ready
    const timer = setTimeout(fixAndAssignPod, 1000);
    return () => clearTimeout(timer);
  }, [user]);
};